import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Global side-effect that fires the GA4 "Signup_successful" event whenever
 * any page in the app is opened with `?signup=success` in its URL.
 *
 * Why this exact name?
 *   The Google Ads conversion action ("Successful signups") is **imported
 *   from GA4**, not a native Google Ads tag. That means there is no
 *   `AW-XXXX/label` to fire against — the conversion is matched purely on
 *   the GA4 event name. The event name MUST be exactly `Signup_successful`
 *   (case-sensitive). Any drift (`sign_up`, `signup`, `signupSuccessful`,
 *   etc.) and Google Ads will not register the conversion.
 *
 * The URL marker is stamped by AuthCallback for first-time users — they
 * land back on the page they originally clicked sign-in from (an astrologer
 * profile, /astrologers, the home page, etc.), and this hook fires the
 * conversion event without disturbing the user's natural flow.
 *
 * Dedup safety: we keep the last-fired signup id in sessionStorage so a
 * SPA route re-render or a back-button doesn't double-fire the conversion.
 *
 * The query param is removed via history.replaceState right after firing
 * so the URL stays clean for sharing / bookmarking.
 */
export default function useSignupConversion() {
  const location = useLocation();
  const firedRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("signup") !== "success") return;
    if (firedRef.current) return;            // ignore re-renders within same mount
    // Persistent dedupe across navigations / soft reloads within a session.
    let alreadyFired = false;
    try { alreadyFired = sessionStorage.getItem("ak_signup_conv_fired") === "1"; } catch (_) { /* storage blocked */ }
    if (alreadyFired) {
      // Still strip the marker so the URL stays clean.
      params.delete("signup");
      const remaining = params.toString();
      const newUrl = location.pathname + (remaining ? `?${remaining}` : "") + (location.hash || "");
      try { window.history.replaceState(window.history.state, "", newUrl); } catch (_) { /* ignore */ }
      return;
    }

    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      // EXACT event name `Signup_successful` so the GA4 → Google Ads
      // import recognises it. Do not rename without also updating the
      // conversion action in Google Ads.
      window.gtag("event", "Signup_successful", {
        method: "google",
        currency: "INR",
        value: 100,
        // Helps GA4 deduplicate if the event somehow fires twice on the
        // same browser session.
        transaction_id: `sig_${Date.now()}_${Math.floor(Math.random() * 1e6)}`,
      });
      // Also fire the GA4 standard `sign_up` event so analytics dashboards
      // / funnel reports continue to work. This is purely supplementary —
      // the conversion match happens on `Signup_successful` above.
      window.gtag("event", "sign_up", { method: "google" });
    }

    firedRef.current = true;
    try { sessionStorage.setItem("ak_signup_conv_fired", "1"); } catch (_) { /* ignore */ }

    // Strip the marker so subsequent renders / shares stay clean.
    params.delete("signup");
    const remaining = params.toString();
    const newUrl = location.pathname + (remaining ? `?${remaining}` : "") + (location.hash || "");
    try {
      window.history.replaceState(window.history.state, "", newUrl);
    } catch (_) { /* ignore — history API may be restricted */ }
  }, [location.pathname, location.search, location.hash]);
}
